<template>
  <div class="notification-bell" ref="bellRef">
    <button class="bell-button" @click="toggle" :class="{ 'bell-button--active': open }">
      <Bell class="bell-icon" />
      <Transition name="badge">
        <span v-if="summary.total > 0" class="bell-badge">
          {{ summary.total > 99 ? '99+' : summary.total }}
        </span>
      </Transition>
      <span v-if="summary.total > 0" class="bell-ping" />
    </button>

    <Transition name="dropdown">
      <div v-if="open" class="dropdown" @click.stop>
        <!-- Header -->
        <div class="dropdown-header">
          <span class="dropdown-title">{{ $gt('Benachrichtigungen') }}</span>
          <span v-if="summary.total > 0" class="dropdown-count">{{ summary.total }}</span>
        </div>

        <!-- Vertretungshinweis (Kundenfeedback Kanzlei 08.09.2026, I): Der
             Vertreter sieht, wen er aktuell vertritt; die Akten der
             Vertretenen sind für ihn in der Fallliste sichtbar. -->
        <button v-if="substituting.length" class="substitute-banner" type="button" @click="goToCases">
          <UserRoundCheck class="substitute-icon" />
          <span class="substitute-text">
            <span class="substitute-title">{{ $gt('Sie vertreten aktuell') }}</span>
            <span class="substitute-names">{{ substitutingLabel }}</span>
          </span>
        </button>

        <!-- Items (Issue #618: nur Kommunikation – eingehende Nachrichten/E-Mails) -->
        <div class="dropdown-items" v-if="summary.items.length > 0">
          <button
            v-for="(item, i) in summary.items"
            :key="i"
            class="notif-item"
            :class="{ 'notif-item--urgent': item.isUrgent }"
            @click="goToItem(item)"
          >
            <div
              class="notif-icon-wrapper"
              :class="item.isUrgent ? 'notif-icon--urgent' : `notif-icon--${item.type}`"
            >
              <component :is="iconForItem(item)" class="notif-icon" :class="{ 'notif-icon--pulse': item.isUrgent }" />
            </div>
            <div class="notif-content">
              <span class="notif-title-row">
                <span class="notif-title">{{ item.title }}</span>
                <span v-if="item.isUrgent" class="notif-urgent-badge">{{ $gt('Dringend') }}</span>
              </span>
              <span class="notif-subtitle">{{ item.subtitle }}</span>
              <span v-if="item.caseNumber" class="notif-case">{{ item.caseNumber }}</span>
            </div>
            <span v-if="item.timeAgo" class="notif-time">{{ formatTime(item.timeAgo) }}</span>
          </button>
        </div>

        <!-- Empty state -->
        <div v-else class="dropdown-empty">
          <BellOff class="empty-icon" />
          <span>{{ $gt('Keine Benachrichtigungen') }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { Bell, BellOff, MessageCircle, AlertTriangle, UserRoundCheck } from "lucide-vue-next";
import { describeSubstitutionScope } from "@/services/substitutionApi";
import { getNotificationSummary } from "@/services/notificationApi";
import { getRequestGeneric } from "@/services/backendApiGeneric";
import { groupInboxItems } from "@/components/messages/inboxGrouping";
import { useAuthStore } from "@/stores/authStore.ts";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
// Eskalation dringender Nachrichten: Nach jedem Poll werden Items mit
// isUrgent === true an das globale UrgentMessageAlert-Overlay gemeldet.
import { reportUrgentItems } from "@/components/app/UrgentMessageAlert.vue";

// Kundenfeedback 29.08.26, III: Neue Nachrichten müssen schneller auffallen —
// kürzeres Poll-Intervall + Popup (Snackbar mit "Öffnen") bei neuen Einträgen.
const POLL_INTERVAL_MS = 30000;

export default {
  components: { Bell, BellOff, MessageCircle, UserRoundCheck },

  data() {
    return {
      open: false,
      summary: {
        unreadMessages: 0,
        total: 0,
        items: [],
      },
      pollTimer: null,
      // Konversations-IDs des letzten Polls. null = noch kein Poll gelaufen;
      // beim allerersten Poll nach dem Mount wird kein Popup gezeigt.
      knownConversationIds: null,
    };
  },

  computed: {
    substituting() {
      return Array.isArray(this.summary.substituting) ? this.summary.substituting : [];
    },
    substitutingLabel() {
      // Fallauswahl der Regel (Kundenfeedback 13.09.26, I) mit anzeigen,
      // z. B. „Konermann (nur ungerade Fallnummern, bis 20.09.2026)“.
      return this.substituting
        .map((s) => {
          const parts = [];
          const scope = describeSubstitutionScope(s.caseScope, s.caseScopeRule);
          if (scope) parts.push(scope);
          if (s.validUntil) parts.push(`bis ${this.formatDate(s.validUntil)}`);
          return parts.length ? `${s.name} (${parts.join(", ")})` : s.name;
        })
        .join(", ");
    },
  },

  mounted() {
    this.fetchNotifications();
    this.pollTimer = setInterval(() => this.fetchNotifications(), POLL_INTERVAL_MS);
    document.addEventListener("click", this.onClickOutside);
  },

  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    document.removeEventListener("click", this.onClickOutside);
  },

  methods: {
    async fetchNotifications() {
      try {
        const summary = await getNotificationSummary();
        if (!summary.items) summary.items = [];

        const orgId = useAuthStore().idOrganization;
        if (orgId) {
          const inboxItems = await getRequestGeneric(`/protected/organizations/${orgId}/inbox`);
          // Nur ungelesene offene Einträge zählen – gelesene Post bleibt im
          // Posteingang, meldet sich aber nicht mehr (Kundenfeedback 11.09.26, V.).
          // Eine E-Mail mit mehreren Anhängen zählt einmal, nicht je Anhang
          // (Kundenfeedback 13.09.26: "zwei offene E-Mails, obwohl es eine ist").
          const pendingItems = groupInboxItems((Array.isArray(inboxItems) ? inboxItems : []).filter(
            (item) => (item.status === "pending" || item.status === "failed") && !item.isRead,
          )).map((g) => g.primary);
          const mappedItems = pendingItems.slice(0, 5).map((item) => ({
            type: "message",
            title: item.subject || (item.source === "scan" || item.source === "upload" ? "Unzugeordnetes Dokument" : "Unzugeordnete E-Mail"),
            subtitle: item.source === "scan" || item.source === "upload"
              ? `${item.filename || "Scan"} wartet auf Zuordnung`
              : (item.fromAddress || "Eingehende E-Mail wartet auf Zuordnung"),
            caseId: 0,
            caseNumber: item.suggestedCaseNumber || "Posteingang",
            caseDescription: item.suggestedCaseDescription || "Zuordnung erforderlich",
            timeAgo: item.receivedAt,
            icon: "MessageCircle",
            inboxId: item.id,
          }));

          summary.unreadMessages += pendingItems.length;
          summary.total += pendingItems.length;
          summary.items = mappedItems.concat(summary.items);
        }

        this.summary = summary;

        // Dringende Einträge an das Popup melden (defensiv: fehlt isUrgent,
        // bleibt die Liste leer und nichts ändert sich am bisherigen Verhalten).
        reportUrgentItems(summary.items);

        // Popup bei neu aufgetauchten Fall-Nachrichten (Feedback 29.08.26, III).
        this.notifyNewConversations(summary.items);
      } catch {
        // silent — bell just shows 0
      }
    },

    /**
     * Vergleicht die fallbezogenen Konversations-Items mit dem letzten Poll
     * und zeigt für neu aufgetauchte Einträge EINEN Snackbar mit "Öffnen",
     * der direkt in die Fall-Unterhaltung führt (Feedback 29.08.26, III).
     * Der erste Poll nach dem Mount baut nur die Vergleichsbasis auf.
     */
    notifyNewConversations(items) {
      const convItems = (Array.isArray(items) ? items : []).filter(
        (i) => i.idConversation && i.caseId,
      );
      const currentIds = new Set(convItems.map((i) => String(i.idConversation)));

      const previousIds = this.knownConversationIds;
      this.knownConversationIds = currentIds;
      if (previousIds === null) return;

      const newItems = convItems.filter(
        (i) => !previousIds.has(String(i.idConversation)),
      );
      if (newItems.length === 0) return;

      const first = newItems[0];
      const message =
        newItems.length === 1
          ? `Neue Nachricht im Fall ${first.caseNumber || first.caseId}`
          : `${newItems.length} neue Nachrichten`;
      useSnackbarStore().showSnackbar({
        message,
        actionLabel: "Öffnen",
        onAction: () => this.goToItem(first),
      });
    },

    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.fetchNotifications();
      }
    },

    onClickOutside(e) {
      if (this.open && this.$refs.bellRef && !this.$refs.bellRef.contains(e.target)) {
        this.open = false;
      }
    },

    iconForType(type) {
      switch (type) {
        case "message": return MessageCircle;
        default: return Bell;
      }
    },

    iconForItem(item) {
      // Dringende Nachrichten bekommen ein rotes Warn-Icon statt des Typ-Icons.
      if (item.isUrgent) return AlertTriangle;
      return this.iconForType(item.type);
    },

    goToItem(item) {
      this.open = false;
      // Noch nicht zugeordnete Posteingangs-Mail → globale Nachrichtenansicht,
      // direkt auf diese Mail (Deep-Link via ?inbox=).
      if (item.inboxId) {
        this.$router.push({ name: "PageMessages", query: { inbox: String(item.inboxId) } });
        return;
      }
      // Fallbezogene Nachricht → Fall-Kommunikation, direkt in die konkrete
      // Unterhaltung (Deep-Link via ?conversation=, Issue #618).
      const query = item.idConversation ? { conversation: String(item.idConversation) } : undefined;
      this.$router.push({ name: "PageCaseDetailNachrichten", params: { idCase: item.caseId }, query });
    },

    goToCases() {
      this.open = false;
      this.$router.push({ name: "PageCases" });
    },

    formatDate(iso) {
      if (!iso) return "";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return y && m && d ? `${d}.${m}.${y}` : iso;
    },

    formatTime(raw) {
      if (!raw) return "";
      // If it's a date string like "2025-01-15", show as DD.MM.
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return raw;
        const now = new Date();
        const diff = now.getTime() - d.getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "Gerade eben";
        if (mins < 60) return `vor ${mins} Min.`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `vor ${hours} Std.`;
        const days = Math.floor(hours / 24);
        if (days === 1) return "Gestern";
        if (days < 7) return `vor ${days} Tagen`;
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
      }
      return raw;
    },
  },
};
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 12px;
}

/* ── Bell button ── */
.bell-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s;
}

.bell-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.bell-button--active {
  background-color: rgba(255, 255, 255, 0.14);
}

.bell-icon {
  width: 20px;
  height: 20px;
  color: rgba(255, 255, 255, 0.85);
  stroke-width: 1.5;
}

/* ── Badge ── */
.bell-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ef4444;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
}

.bell-ping {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ef4444;
  opacity: 0.6;
  animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  pointer-events: none;
}

@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

/* Badge transition */
.badge-enter-active,
.badge-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.badge-enter-from,
.badge-leave-to {
  transform: scale(0);
  opacity: 0;
}

/* ── Dropdown ── */
.dropdown {
  position: fixed;
  top: 68px;
  right: 16px;
  width: 380px;
  max-height: 520px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.dropdown-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.dropdown-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* ── Header ── */
.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 10px;
}

.dropdown-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1b2e;
}

.dropdown-count {
  background: #ef4444;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}

/* ── Vertretungshinweis ── */
.substitute-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 12px 8px;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  background: #ecfdf5;
  box-shadow: inset 0 0 0 1px #a7f3d0;
  color: #065f46;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s;
}

.substitute-banner:hover {
  background: #d1fae5;
}

.substitute-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  stroke-width: 1.75;
}

.substitute-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.substitute-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.substitute-names {
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Item list ── */
.dropdown-items {
  overflow-y: auto;
  flex: 1;
  padding: 0 8px 8px;
}

.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.12s;
}

.notif-item:hover {
  background-color: #f3f4f6;
}

.notif-icon-wrapper {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notif-icon {
  width: 18px;
  height: 18px;
  stroke-width: 1.75;
}

.notif-icon--message {
  background: #dbeafe;
  color: #2563eb;
}

/* ── Dringende Einträge (Eskalation) ── */
.notif-item--urgent {
  background: #fef2f2;
  box-shadow: inset 0 0 0 1px #fecaca;
}

.notif-item--urgent:hover {
  background-color: #fee2e2;
}

.notif-icon--urgent {
  background: #fee2e2;
  color: #dc2626;
}

.notif-icon--pulse {
  animation: urgent-icon-pulse 1.6s ease-in-out infinite;
}

@keyframes urgent-icon-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

.notif-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.notif-title-row .notif-title {
  flex: 1;
  min-width: 0;
}

.notif-urgent-badge {
  flex-shrink: 0;
  padding: 1px 7px;
  border-radius: 8px;
  background: #dc2626;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1.5;
}

.notif-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.notif-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-case {
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 500;
}

.notif-time {
  flex-shrink: 0;
  font-size: 0.72rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-top: 2px;
}

/* ── Empty ── */
.dropdown-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
  gap: 8px;
}

.empty-icon {
  width: 32px;
  height: 32px;
  stroke-width: 1.25;
}

.dropdown-empty span {
  font-size: 0.85rem;
}
</style>
